import { Box, Button, styled, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { FormField } from '../../../../components/FormField/FormField';
import { Multiselect } from '../../../../components/Multiselect/Multiselect';
import { SingleSelect } from '../../../../components/SingleSelect/SingleSelect';
import { SelectItem, UserRole } from '../../../../types';
import { IUserFileds } from './UserModal';
import { isEndDateInvalid } from './isEndDateInvalid';
import { UpdateUserSkeletonLoader } from '../../../../components/SkeletonLoader/UpdateUser.SkeletonLoader';
import { useUsers } from '../../../../queries/hooks/useUsers';
import { useDeals } from '../../../../queries/hooks/useDeals';
import { useSelectedWorkflow } from './useSelectedWorkflow';
import { SvpManagerField } from './SvpManagerField';
import { DatePicker } from '../../../../components/DatePicker/DatePicker';
import { addDaysToDate, formatDateToString } from '../../../../utils/formatters';
import { Typography } from '../../../../components/Typography/Typography';
import { ReactComponent as USAFlag } from '../../../../assets/icons/usa-flag.svg';
import { ReactComponent as UKFlag } from '../../../../assets/icons/uk-flag.svg';
import { ConfirmModal } from '../../../../components/ConfirmModal/ConfirmModal';
import { hasRole } from '../../../../utils/roles';

export function OperatingPartnerFields() {
  const { colors } = useTheme();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | string>('');
  const { data: dealOptionsRaw, isLoading: dealOptionsLoading } = useDeals(false, UserRole.ADMIN);
  const { isLoading: areUsersLoading, data: users } = useUsers();

  const { setFieldValue, values, touched, errors, setFieldTouched, initialValues } =
    useFormikContext<IUserFileds>();

  // The Managers picker is shown whenever the selected workflow has a MANAGER approval stage (so a
  // Manager can approve), regardless of whether the owner is a pure OP or an isOp OP Manager.
  const { hasManagerStage } = useSelectedWorkflow();

  const handleError = (field) => (touched[field] ? errors[field] : '');

  const dealsOptions: SelectItem[] = useMemo(() => {
    if (!dealOptionsRaw) return [];
    return dealOptionsRaw?.map((deal) => {
      return {
        ...deal,
        id: deal.id,
        value: deal?.name || '',
      };
    });
  }, [dealOptionsRaw]);

  // SVP-826: a user holding both Submitter and Proxy could pick themselves here, which the API rejects
  // outright — a proxy acts on somebody else's behalf. Dropping them from the options makes it
  // unselectable instead of a save-time error, same as the Managers picker already does.
  const proxyOptions = useMemo(() => {
    if (!users) return [];
    return users
      .filter((user) => hasRole(user, UserRole.PROXY) && user.email !== values.email)
      .map((user) => ({
        id: user.id,
        value: user.name,
      }));
  }, [users, values.email]);

  const managersOptions = useMemo(() => {
    if (!users) return [];
    return users
      .filter((user) => hasRole(user, UserRole.OP_MANAGER) && user.email !== values.email)
      .map((user) => ({
        id: user.id,
        value: user.name,
      }));
  }, [users, values.email]);

  const labelStyle = {
    color: colors.text.caption,
  };

  const minDate = useMemo(() => {
    if (values.startDate && new Date(values.startDate) > new Date()) {
      return formatDateToString(addDaysToDate(values.startDate, 1));
    }

    return formatDateToString(new Date());
  }, [values.startDate]);

  /** MUI DatePicker errors the input when value < minDate; relax min while showing unchanged server end date in the past */
  const endDatePickerMinDate = useMemo(() => {
    const unchanged = (values.endDate ?? null) === (initialValues.endDate ?? null);
    if (!values.endDate || !unchanged) return minDate;

    const endMid = new Date(values.endDate);
    endMid.setHours(0, 0, 0, 0);
    const policyMid = new Date(minDate);
    policyMid.setHours(0, 0, 0, 0);
    if (endMid < policyMid) {
      return values.endDate;
    }
    return minDate;
  }, [values.endDate, initialValues.endDate, minDate]);

  const maxDate = useMemo(() => {
    if (values.endDate) return formatDateToString(addDaysToDate(values.endDate, -1));
    return null;
  }, [values.endDate]);

  const showEndDateError = useMemo(
    () =>
      isEndDateInvalid({
        endDate: values.endDate,
        startDate: values.startDate,
        initialEndDate: initialValues.endDate,
      }),
    [values.startDate, values.endDate, initialValues.endDate]
  );

  const loadingFieldsOptions = dealOptionsLoading || areUsersLoading;

  return loadingFieldsOptions ? (
    <UpdateUserSkeletonLoader />
  ) : (
    <>
      {hasManagerStage && (
        <FormField label={'Managers *'} error={handleError('managers')} labelStyle={labelStyle}>
          <Multiselect
            listboxStyle={{ height: '195px' }}
            options={managersOptions}
            value={values?.managers as SelectItem[]}
            onChange={(_, managers) => {
              setFieldValue('managers', managers);
            }}
            onBlur={() => {
              setFieldTouched('managers');
            }}
            disablePortal
            fieldPlaceholder='Add manager...'
          />
          <Typography variant='caption' color={colors.text.secondary}>
            Approve submitted timesheets.
          </Typography>
        </FormField>
      )}
      {/* SVP Managers picker sits directly under the Managers picker; self-gated by hasSvpStage. */}
      <SvpManagerField />
      <Box>
        <Typography variant='subtitle2' color={colors.text.main} style={{ marginBottom: 4 }}>
          Timesheet period
        </Typography>
        <Typography variant='caption' color={colors.text.secondary} style={{ display: 'block', marginBottom: 10 }}>
          Timesheets are issued between these dates. A new timesheet is issued on the first day of each week.
        </Typography>
        <DateWrapper>
          <FormField
            label={'Start work date *'}
            error={handleError('startDate')}
            labelStyle={labelStyle}
          >
            <Box minHeight='56px'>
              <DatePicker
                clearable
                selectedDate={values.startDate ?? ''}
                setSelectedDate={(date) => {
                  if (date) {
                    const dateString = formatDateToString(date);
                    setOpenConfirmModal(true);
                    setSelectedDate(dateString);
                  } else {
                    setFieldValue('startDate', date);
                  }
                }}
                placeholder='Start Date'
                minDate={new Date('2019-01-01').toISOString()}
                maxDate={maxDate}
              />
            </Box>
          </FormField>

          <FormField label='End work date' labelStyle={labelStyle}>
            <Box
              style={{
                minHeight: '56px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <DatePicker
                clearable
                selectedDate={values.endDate ?? ''}
                setSelectedDate={(date) => {
                  const dateString = date ? formatDateToString(date) : null;
                  setFieldValue('endDate', dateString);
                }}
                placeholder='Ongoing'
                maxDate={null}
                minDate={endDatePickerMinDate}
              />
              {showEndDateError && (
                <Typography variant='caption' color={colors.iconStatus.error}>
                  End Date cannot be in the past
                </Typography>
              )}
            </Box>
          </FormField>
        </DateWrapper>
        <FormField label='Country *' labelStyle={labelStyle} error={handleError('country')}>
          <CountryWrapper>
            <Button
              variant={values?.country === 'US' ? 'contained' : 'outlined'}
              onClick={() => {
                setFieldValue('country', 'US');
              }}
              style={{
                backgroundColor: values?.country === 'US' ? '' : 'transparent',
                borderRadius: '4px 0 0 4px',
              }}
            >
              <USAFlag />
              <Typography
                variant='body'
                color={colors.text.main}
                style={{
                  marginLeft: '4px',
                }}
              >
                US
              </Typography>
            </Button>
            <Button
              variant={values?.country === 'UK' ? 'contained' : 'outlined'}
              onClick={() => {
                setFieldValue('country', 'UK');
              }}
              style={{
                backgroundColor: values?.country === 'UK' ? '' : 'transparent',
                borderRadius: '0 4px 4px 0',
              }}
            >
              <UKFlag />
              <Typography
                variant='body'
                color={colors.text.main}
                style={{
                  marginLeft: '4px',
                }}
              >
                UK
              </Typography>
            </Button>
          </CountryWrapper>
        </FormField>
      </Box>
      <FormField
        label={'Deals'}
        error={handleError('operatingPartnerDeals')}
        labelStyle={labelStyle}
      >
        <Multiselect
          listboxStyle={{ height: '195px' }}
          options={dealsOptions}
          value={values?.operatingPartnerDeals as SelectItem[]}
          onChange={(_, deals) => {
            setFieldValue('operatingPartnerDeals', deals);
          }}
          onBlur={() => {
            setFieldTouched('operatingPartnerDeals');
          }}
          disablePortal
          fieldPlaceholder='Select deals'
        />
      </FormField>

      <FormField label={'Proxy'} error={handleError('proxy')} labelStyle={labelStyle}>
        <SingleSelect
          listboxStyle={{ height: '195px' }}
          options={proxyOptions}
          value={values?.proxy}
          onChange={(_, option) => {
            setFieldValue('proxy', option);
          }}
          onBlur={() => {
            setFieldTouched('proxy');
          }}
          disablePortal
          fieldPlaceholder='Select proxy'
        />
        <Typography variant='caption' color={colors.text.secondary}>
          {`Can submit timesheets on this person's behalf.`}
        </Typography>
      </FormField>

      <ConfirmModal
        open={openConfirmModal}
        title='Edit Start Work Date'
        note='Changing Start Work Date will result in deleting all Timesheets created before. Are you sure you want to do that?'
        onClose={() => {
          setFieldValue('startDate', initialValues.startDate);
          setOpenConfirmModal(false);
        }}
        onConfirm={() => {
          setFieldValue('startDate', selectedDate);
          setOpenConfirmModal(false);
        }}
      />
    </>
  );
}

const DateWrapper = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: end;
  gap: 12px;
  .MuiFormControl-root {
    width: 100%;
  }
`;

const CountryWrapper = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
`;
